import { Button, PageHeader } from "@majitap/ui";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import {
  listQueries,
  openShifts,
  PROFILE_STATUS,
  TRUCK_STATUS,
  type TruckStatus,
  VIEW_LABEL,
  VIEWS,
  type View,
} from "../../fleet";
import { APPROVAL_STATUS, type ApprovalStatus } from "../../labels";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import { DriversView } from "./drivers";
import styles from "./fleet.module.css";
import { ShiftsView } from "./shifts";
import { AssistantsView, FillsitesView } from "./sites";
import { TrucksView } from "./trucks";

export const fleetFilters = filterSearch({
  view: VIEWS,
  q: "text",
  status: Object.keys(TRUCK_STATUS) as TruckStatus[],
  approval: Object.keys(APPROVAL_STATUS) as ApprovalStatus[],
  // whose: "own", "partners" or one partner's id (every view)
  owner: "text",
  // an assistant's status
  state: Object.keys(PROFILE_STATUS) as (keyof typeof PROFILE_STATUS)[],
  truck: "text",
  // the Shifts view: which shifts, started on which day, and one open beside the list
  shifts: ["closed", "all"] as const,
  date: "text",
  shift: "text",
});
export type FleetSearch = ReturnType<typeof fleetFilters.validate>;

const ADD: Partial<Record<View, string>> = {
  trucks: "Add truck",
  drivers: "Add driver",
  assistants: "Add assistant",
  fillsites: "Add fill site",
  shifts: "Open shift",
};

/**
 * Fleet (P8-12, `Console / Fleet` in the design): one page, five lists picked with a segmented
 * control, the view in the URL. Anyone on staff sees them (dispatchers assign from them);
 * changes need `fleet.manage`, reviewing a partner's fill site `partners.manage`.
 */
export function FleetPage(): ReactNode {
  const search = useSearch({ from: "/console/fleet" });
  const navigate = useNavigate({ from: "/fleet" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const can = {
    manage: permissions.includes("fleet.manage"),
    partners: permissions.includes("partners.manage"),
    wallets: permissions.includes("payments.record"),
    float: permissions.includes("wallets.manage"),
    payouts: permissions.includes("payouts.approve"),
  };
  const view = search.view ?? "trucks";
  const shifts = useQuery(listQueries.shifts);
  const onShift = shifts.data ? openShifts(shifts.data).length : 0;
  const [adding, setAdding] = useState(false);
  const set = (patch: Partial<FleetSearch>, replace = false) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace });
  const add = can.manage ? ADD[view] : undefined;

  return (
    <>
      <PageHeader
        title="Fleet"
        description="Trucks, drivers, assistants and fill sites, yours and your partners'."
        actions={
          <>
            <RouterLink href="/fleet/map" className={styles.link}>
              Live map
            </RouterLink>
            {add ? <Button onClick={() => setAdding(true)}>{add}</Button> : null}
          </>
        }
      />
      <SegmentedControl
        label="Show"
        value={view}
        // a new view starts unfiltered: each view has its own filters
        onValueChange={(v) => navigate({ search: { view: v === "trucks" ? undefined : (v as View) } })}
        options={VIEWS.map((v) => ({
          value: v,
          label: v === "shifts" && onShift > 0 ? `${VIEW_LABEL[v]} · ${onShift}` : VIEW_LABEL[v],
        }))}
      />
      {view === "trucks" ? (
        <TrucksView search={search} set={set} can={can} adding={adding} onAdded={() => setAdding(false)} />
      ) : null}
      {view === "drivers" ? (
        <DriversView search={search} set={set} can={can} adding={adding} onAdded={() => setAdding(false)} />
      ) : null}
      {view === "assistants" ? (
        <AssistantsView search={search} set={set} can={can} adding={adding} onAdded={() => setAdding(false)} />
      ) : null}
      {view === "fillsites" ? (
        <FillsitesView search={search} set={set} can={can} adding={adding} onAdded={() => setAdding(false)} />
      ) : null}
      {view === "shifts" ? (
        <ShiftsView search={search} set={set} can={can} adding={adding} onAdded={() => setAdding(false)} />
      ) : null}
    </>
  );
}

export interface FleetCan {
  /** `fleet.manage`: add, edit, retire, approve drivers, open and close shifts, change the shift checklist. */
  manage: boolean;
  /** `partners.manage`: partner names, and reviewing a partner's fill sites. */
  partners: boolean;
  /** `payments.record`: a driver's wallet. */
  wallets: boolean;
  /** `wallets.manage`: a cash float for a driver with no wallet yet. */
  float: boolean;
  /** `payouts.approve`: a driver's payout requests. */
  payouts: boolean;
}
